import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Button, Modal, Select, InputNumber, Input, Spin, Empty } from "antd";
import { toast } from "../toasts/toastStore";
import { apiGeneralInstance, authHeaderGeneral } from "../../redux/actions/general/general";

const money = (n) =>
  n == null ? "—" : `$${parseFloat(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const RESOLUCIONES = [
  { value: "reembolso", label: "Reembolso" },
  { value: "nota_credito", label: "Nota de credito" },
  { value: "cambio", label: "Cambio de producto" },
];
const RES_LABEL = Object.fromEntries(RESOLUCIONES.map((r) => [r.value, r.label]));

export default function DevolucionesProveedor({ compra, onChanged }) {
  const idCompra = compra.id_compra;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchItems = useCallback(() => {
    apiGeneralInstance.get(`/devoluciones/proveedor/${idCompra}`, { headers: authHeaderGeneral() })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("No se pudieron cargar las devoluciones"))
      .finally(() => setLoading(false));
  }, [idCompra]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const lineas = compra.detalle || [];
  const devuelto = items.reduce((acc, d) => { acc[d.id_variante] = (acc[d.id_variante] || 0) + parseFloat(d.cantidad); return acc; }, {});
  const disponible = (l) => parseFloat(l.cantidad) - (devuelto[l.id_variante] || 0);
  const nombreLinea = (l) => `${l.producto_nombre || l.item_nombre || l.nombre || "Producto"}${l.clave ? ` (${l.clave})` : ""}`;
  const lineaSel = lineas.find((l) => l.id_variante === form.id_variante);

  const handleSave = async () => {
    if (!form.id_variante) return toast.warning("Selecciona el producto");
    if (!form.cantidad || form.cantidad <= 0) return toast.warning("Indica la cantidad");
    if (!form.resolucion) return toast.warning("Indica la resolucion");
    if (!form.motivo?.trim()) return toast.warning("El motivo es obligatorio");
    setSaving(true);
    try {
      const res = await apiGeneralInstance.post(`/devoluciones/proveedor/${idCompra}`, form, { headers: authHeaderGeneral() });
      setItems(res.data?.items || []);
      toast.success("Devolucion registrada");
      setOpen(false);
      setForm({});
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la devolucion");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cd-card">
      <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="cd-card-icon-wrap"><i className="fa-solid fa-rotate-left"></i></div>
          <h2 className="cd-card-title">Devoluciones al proveedor</h2>
          <span className="cd-actividad-count">{items.length}</span>
        </div>
        {!compra.cancelada && lineas.length > 0 && (
          <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => { setForm({}); setOpen(true); }}
            style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>Nueva devolucion</Button>
        )}
      </div>

      {loading ? <Spin /> : items.length === 0 ? <Empty description="Sin devoluciones" /> : (
        <div>
          {items.map((d) => (
            <div key={d.id_devolucion_proveedor} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
              <div>
                <span className="cd-field-label">Producto</span>
                <div className="cd-field-value">{d.producto_nombre}{d.clave ? ` (${d.clave})` : ""} x {parseFloat(d.cantidad)}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{d.motivo}</div>
              </div>
              <div><span className="cd-field-label">Resolucion</span><div className="cd-field-value">{RES_LABEL[d.resolucion] || d.resolucion}</div></div>
              <div><span className="cd-field-label">Monto</span><div className="cd-field-value">{d.monto != null ? money(d.monto) : "—"}</div></div>
              <div>
                <span className="cd-field-label">Registro</span>
                <div className="cd-field-value">{d.creado_por}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{dayjs(d.datetime).format("D MMM YYYY, HH:mm")}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal title="Nueva devolucion al proveedor" open={open} onCancel={() => setOpen(false)} onOk={handleSave} confirmLoading={saving} okText="Registrar" cancelText="Cancelar" okButtonProps={{ style: { backgroundColor: "#f90336", borderColor: "#f90336" } }}>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div className="cd-field-label">Producto</div>
            <Select style={{ width: "100%" }} placeholder="Selecciona el producto" value={form.id_variante}
              onChange={(v) => setForm({ ...form, id_variante: v, cantidad: undefined })}
              options={lineas.map((l) => ({ value: l.id_variante, disabled: disponible(l) <= 0, label: `${nombreLinea(l)} (disponible: ${disponible(l)})` }))} />
          </div>
          <div>
            <div className="cd-field-label">Cantidad</div>
            <InputNumber style={{ width: "100%" }} min={0} max={lineaSel ? disponible(lineaSel) : undefined} value={form.cantidad} onChange={(v) => setForm({ ...form, cantidad: v })} />
          </div>
          <div>
            <div className="cd-field-label">Resolucion</div>
            <Select style={{ width: "100%" }} value={form.resolucion} onChange={(v) => setForm({ ...form, resolucion: v })} options={RESOLUCIONES} />
          </div>
          <div>
            <div className="cd-field-label">Monto (opcional)</div>
            <InputNumber style={{ width: "100%" }} min={0} prefix="$" value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} />
          </div>
          <div>
            <div className="cd-field-label">Motivo</div>
            <Input.TextArea rows={2} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
